import React from 'react';
import {
  Box, IconButton, Typography, useMediaQuery, useTheme, Divider,
  Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { InvTextField } from '../InvField';
import InvItemAutocomplete from '../InvItemAutocomplete';
import PoItemAutocomplete from '../PoItemAutocomplete';
import { formatCurrency, formatQty, lineTotal, computeLineDiscountAmount } from '../../utils';
import { minGrnExpiryDate, isPoRateOverridden } from '../../utils/grnEntity';
import { IPD_COLORS, cardSx } from '../../theme';

const FIELD_MIN_H = 40;

const actionColSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
};

function focusGrnLineItemField(lineNumber) {
  setTimeout(() => {
    const root = document.querySelector(`[data-grn-line="${lineNumber}"]`);
    if (!root) return;
    const input = root.querySelector('[data-grn-focus="item"] input');
    if (input) {
      input.focus();
      return;
    }
    root.querySelector('[data-grn-focus="item"] [role="combobox"]')?.focus();
  }, 80);
}

function handleDiscPercentKeyDown(e, index, onAdd) {
  if (e.key !== 'Tab' || e.shiftKey) return;
  e.preventDefault();
  onAdd(index);
  focusGrnLineItemField(index + 2);
}

function isLineExpiryPast(line, todayDate) {
  return Boolean(line.expiry_date && line.expiry_date <= todayDate);
}

function ExpiredOverrideControl({ line, todayDate, onToggle }) {
  if (!isLineExpiryPast(line, todayDate)) return null;
  return (
    <FormControlLabel
      control={(
        <Checkbox
          size="small"
          checked={Boolean(line.allow_expired_override)}
          onChange={e => onToggle(e.target.checked)}
        />
      )}
      label={(
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, lineHeight: 1.3 }}>
          Accept expired stock (override)
        </Typography>
      )}
      sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
    />
  );
}

const readOnlyBoxSx = {
  minHeight: FIELD_MIN_H,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  border: `1px solid ${IPD_COLORS.border}`,
  backgroundColor: IPD_COLORS.selectedBg || '#f8fafb',
  display: 'flex',
  alignItems: 'center',
};

const grid12Sx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  gap: 1.5,
  alignItems: 'start',
};

function FieldLabel({ children, required }) {
  return (
    <Typography
      component="label"
      sx={{
        display: 'block',
        color: IPD_COLORS.textSecondary,
        fontSize: 11,
        fontWeight: 500,
        mb: 0.5,
        letterSpacing: 0.2,
      }}
    >
      {children}
      {required ? ' *' : null}
    </Typography>
  );
}

function ReadOnlyValue({ value, align = 'left', warn = false, mono = false }) {
  return (
    <Box sx={readOnlyBoxSx}>
      <Typography sx={{
        width: '100%',
        fontSize: 13,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        textAlign: align,
        color: warn ? IPD_COLORS.error.text : IPD_COLORS.textPrimary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: IPD_COLORS.textSecondary,
        fontSize: 10,
        letterSpacing: 0.8,
        mb: 1,
        mt: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function DeleteLineButton({ canRemove, onRemove, index }) {
  return (
    <IconButton
      size="small"
      onClick={() => onRemove(index)}
      disabled={!canRemove}
      sx={{ color: IPD_COLORS.error.text }}
      aria-label={`Remove line ${index + 1}`}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
}

function AddLineButton({ onAdd, index }) {
  return (
    <IconButton
      size="small"
      onClick={() => onAdd(index)}
      sx={{ color: IPD_COLORS.primary }}
      aria-label={`Add row below line ${index + 1}`}
      title="Add row below (Tab from Disc % also adds)"
    >
      <AddIcon fontSize="small" />
    </IconButton>
  );
}

function DiscPercentField({
  value, onChange, onAdd, index, disabled, error, helperText,
}) {
  return (
    <InvTextField
      value={value}
      onChange={onChange}
      onKeyDown={e => handleDiscPercentKeyDown(e, index, onAdd)}
      type="number"
      inputProps={{ min: 0, max: 100, step: 0.01 }}
      disabled={disabled}
      error={error}
      helperText={helperText}
    />
  );
}

function TotalsBar({ discAmt, rowTotal }) {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 3,
      mt: 1,
      pt: 1.25,
      borderTop: `1px dashed ${IPD_COLORS.border}`,
    }}>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Disc Amt</Typography>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>
          {formatCurrency(discAmt)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Line Total</Typography>
        <Typography sx={{
          fontFamily: 'monospace',
          fontSize: 15,
          fontWeight: 700,
          color: IPD_COLORS.primary,
        }}>
          {formatCurrency(rowTotal)}
        </Typography>
      </Box>
    </Box>
  );
}

function DesktopLineCard({
  line, index, isDirect, uomLabel, poItemOptions, errors, canRemove,
  onRemove, onAdd, onUpdate, onPoItemSelect,
  receivedInt, pendingInt, qtyOverPending, rowTotal, discAmt,
  qtyErr, rateErr, itemErr, batchErr, expiryErr, mfgErr, freeQtyErr,
  mrpErr, saleErr, rateOverrideErr, todayDate, minExpiryDate, canOverridePricing,
  receivingStoreId,
  handleField,
}) {
  const showRateOverride = !isDirect && isPoRateOverridden(line);
  const poRateHint = !isDirect && line.po_unit_rate
    ? `PO rate: ${line.po_unit_rate}`
    : undefined;
  const taxLocked = !isDirect && line.po_tax_percent !== '' && !canOverridePricing;
  const discLocked = !isDirect && line.po_discount_percent !== '' && !canOverridePricing;
  return (
    <Box
      data-grn-line={index + 1}
      sx={{
      display: 'grid',
      gridTemplateColumns: '36px 1fr 36px',
      gap: 1,
      mb: 2,
      p: 1.5,
      ...cardSx,
      backgroundColor: qtyOverPending ? `${IPD_COLORS.warning.text}0d` : undefined,
      borderColor: qtyOverPending ? IPD_COLORS.warning.text : IPD_COLORS.border,
    }}>
      <Box sx={actionColSx}>
        <DeleteLineButton canRemove={canRemove} onRemove={onRemove} index={index} />
      </Box>

      <Box sx={grid12Sx}>
        {/* Row 1 */}
        <Box sx={{ gridColumn: isDirect ? 'span 5' : 'span 4' }} data-grn-focus="item">
          <FieldLabel required>Item</FieldLabel>
          {isDirect ? (
            <InvItemAutocomplete
              value={line.item_id}
              storeId={receivingStoreId}
              onChange={(itemId, item) => onUpdate(index, 'item_id', itemId, item)}
            />
          ) : (
            <PoItemAutocomplete
              value={line.po_item_id || line.purchase_order_item_id}
              options={poItemOptions}
              onChange={poItem => onPoItemSelect(index, poItem)}
            />
          )}
          {itemErr ? (
            <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 11, mt: 0.5 }}>
              {errors.item_id}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <FieldLabel>UOM</FieldLabel>
          <ReadOnlyValue value={uomLabel || '—'} />
        </Box>

        {!isDirect && (
          <Box>
            <FieldLabel>Ordered</FieldLabel>
            <ReadOnlyValue
              value={line.ordered_quantity ? formatQty(line.ordered_quantity) : '—'}
              align="right"
              mono
            />
          </Box>
        )}

        {!isDirect && (
          <Box>
            <FieldLabel>Pending</FieldLabel>
            <ReadOnlyValue
              value={pendingInt != null ? formatQty(pendingInt) : '—'}
              align="right"
              warn={pendingInt === 0}
              mono
            />
          </Box>
        )}

        <Box sx={isDirect ? { gridColumn: 'span 2' } : undefined}>
          <FieldLabel required>Received</FieldLabel>
          <InvTextField
            value={line.received_quantity}
            onChange={e => handleField('received_quantity', e.target.value)}
            type="number"
            inputProps={{
              min: 1,
              step: 1,
              inputMode: 'numeric',
              ...(pendingInt != null ? { max: pendingInt } : {}),
            }}
            error={Boolean(qtyErr)}
            helperText={qtyErr || undefined}
          />
        </Box>

        <Box sx={isDirect ? { gridColumn: 'span 2' } : undefined}>
          <FieldLabel>Free Qty</FieldLabel>
          <InvTextField
            value={line.free_quantity ?? '0'}
            onChange={e => handleField('free_quantity', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            error={Boolean(freeQtyErr)}
            helperText={freeQtyErr || undefined}
          />
        </Box>

        <Box sx={{ gridColumn: isDirect ? 'span 2' : 'span 3' }}>
          <FieldLabel required>Purchase Rate</FieldLabel>
          <InvTextField
            value={line.unit_rate}
            onChange={e => handleField('unit_rate', e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            error={Boolean(rateErr)}
            helperText={rateErr || poRateHint || undefined}
          />
        </Box>

        {showRateOverride ? (
          <Box sx={{ gridColumn: 'span 4' }}>
            <FieldLabel required>Rate Override Reason</FieldLabel>
            <InvTextField
              value={line.rate_override_reason || ''}
              onChange={e => handleField('rate_override_reason', e.target.value)}
              error={Boolean(rateOverrideErr)}
              helperText={rateOverrideErr || 'Required when rate differs from PO'}
            />
          </Box>
        ) : null}

        {/* Row 2 — batch & pricing */}
        <Box sx={{ gridColumn: 'span 2' }}>
          <FieldLabel required={line.is_batch_tracked}>Batch </FieldLabel>
          <InvTextField
            value={line.batch_number}
            onChange={e => handleField('batch_number', e.target.value)}
            error={Boolean(batchErr)}
            helperText={batchErr || undefined}
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          <FieldLabel>Mfg Batch </FieldLabel>
          <InvTextField
            value={line.manufacturer_batch_number || ''}
            onChange={e => handleField('manufacturer_batch_number', e.target.value)}
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          <FieldLabel>Mfg Date</FieldLabel>
          <InvTextField
            value={line.manufacturing_date || ''}
            type="date"
            onChange={e => handleField('manufacturing_date', e.target.value)}
            inputProps={{ max: todayDate }}
            error={Boolean(mfgErr)}
            helperText={mfgErr || undefined}
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          <FieldLabel required={line.is_expiry_tracked}>Expiry Date</FieldLabel>
          <InvTextField
            value={line.expiry_date || ''}
            type="date"
            onChange={e => handleField('expiry_date', e.target.value)}
            inputProps={{ min: line.allow_expired_override ? undefined : minExpiryDate }}
            error={Boolean(expiryErr)}
            helperText={expiryErr || undefined}
          />
          <ExpiredOverrideControl
            line={line}
            todayDate={todayDate}
            onToggle={checked => handleField('allow_expired_override', checked)}
          />
        </Box>

        <Box>
          <FieldLabel required>MRP</FieldLabel>
          <InvTextField
            value={line.mrp || ''}
            onChange={e => handleField('mrp', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            error={Boolean(mrpErr)}
            helperText={mrpErr || undefined}
          />
        </Box>

        <Box>
          <FieldLabel>Sale Rate</FieldLabel>
          <InvTextField
            value={line.sale_rate || ''}
            onChange={e => handleField('sale_rate', e.target.value)}
            type="number"
            inputProps={{
              min: line.unit_rate || 0,
              step: 0.01,
              ...(line.mrp ? { max: line.mrp } : {}),
            }}
            error={Boolean(saleErr)}
            helperText={saleErr || (!line.sale_rate ? 'Defaults to MRP when blank' : undefined)}
          />
        </Box>

        <Box>
          <FieldLabel>Tax %</FieldLabel>
          <InvTextField
            value={line.tax_percent ?? '0'}
            onChange={e => handleField('tax_percent', e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 28, step: 0.01 }}
            disabled={taxLocked}
            error={Boolean(errors?.tax_percent)}
            helperText={errors?.tax_percent || (taxLocked ? 'From PO' : undefined)}
          />
        </Box>

        <Box>
          <FieldLabel>Disc %</FieldLabel>
          <DiscPercentField
            value={line.discount_percent ?? '0'}
            onChange={e => handleField('discount_percent', e.target.value)}
            onAdd={onAdd}
            index={index}
            disabled={discLocked}
            error={Boolean(errors?.discount_percent)}
            helperText={errors?.discount_percent || (discLocked ? 'From PO' : undefined)}
          />
        </Box>

        {/* Totals — row 3 */}
        <Box sx={{ gridColumn: '9 / span 2' }}>
          <FieldLabel>Disc Amt</FieldLabel>
          <ReadOnlyValue value={formatCurrency(discAmt)} align="right" mono />
        </Box>

        <Box sx={{ gridColumn: '11 / span 2' }}>
          <FieldLabel>Line Total</FieldLabel>
          <ReadOnlyValue value={formatCurrency(rowTotal)} align="right" mono />
        </Box>
      </Box>

      <Box sx={actionColSx}>
        <AddLineButton onAdd={onAdd} index={index} />
      </Box>
    </Box>
  );
}

function MobileLineCard({
  line, index, isDirect, uomLabel, poItemOptions, errors, canRemove,
  onRemove, onAdd, onUpdate, onPoItemSelect,
  receivedInt, pendingInt, qtyOverPending, rowTotal, discAmt,
  qtyErr, rateErr, itemErr, batchErr, expiryErr, mfgErr, freeQtyErr,
  mrpErr, saleErr, rateOverrideErr, todayDate, minExpiryDate, canOverridePricing,
  receivingStoreId,
  handleField,
}) {
  const showRateOverride = !isDirect && isPoRateOverridden(line);
  const poRateHint = !isDirect && line.po_unit_rate
    ? `PO rate: ${line.po_unit_rate}`
    : undefined;
  const taxLocked = !isDirect && line.po_tax_percent !== '' && !canOverridePricing;
  const discLocked = !isDirect && line.po_discount_percent !== '' && !canOverridePricing;
  const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 };
  const threeCol = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 };

  return (
    <Box
      data-grn-line={index + 1}
      sx={{
      mb: 2,
      p: 2,
      ...cardSx,
      backgroundColor: qtyOverPending ? `${IPD_COLORS.warning.text}0d` : undefined,
      borderColor: qtyOverPending ? IPD_COLORS.warning.text : IPD_COLORS.border,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteLineButton canRemove={canRemove} onRemove={onRemove} index={index} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: IPD_COLORS.textPrimary }}>
            Line {index + 1}
          </Typography>
        </Box>
        <AddLineButton onAdd={onAdd} index={index} />
      </Box>

      <SectionTitle>Item &amp; Quantities</SectionTitle>
      <Box sx={{ mb: 1.5 }} data-grn-focus="item">
        <FieldLabel required>Item</FieldLabel>
        {isDirect ? (
          <InvItemAutocomplete
            value={line.item_id}
            storeId={receivingStoreId}
            onChange={(itemId, item) => onUpdate(index, 'item_id', itemId, item)}
          />
        ) : (
          <PoItemAutocomplete
            value={line.po_item_id || line.purchase_order_item_id}
            options={poItemOptions}
            onChange={poItem => onPoItemSelect(index, poItem)}
          />
        )}
        {itemErr ? (
          <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 11, mt: 0.5 }}>
            {errors.item_id}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ ...threeCol, mb: 1.5 }}>
        <Box>
          <FieldLabel>UOM</FieldLabel>
          <ReadOnlyValue value={uomLabel || '—'} />
        </Box>
        {!isDirect && (
          <Box>
            <FieldLabel>Ordered</FieldLabel>
            <ReadOnlyValue
              value={line.ordered_quantity ? formatQty(line.ordered_quantity) : '—'}
              align="right"
              mono
            />
          </Box>
        )}
        {!isDirect && (
          <Box>
            <FieldLabel>Pending</FieldLabel>
            <ReadOnlyValue
              value={pendingInt != null ? formatQty(pendingInt) : '—'}
              align="right"
              warn={pendingInt === 0}
              mono
            />
          </Box>
        )}
      </Box>

      <Box sx={{ ...threeCol, mb: 1.5 }}>
        <Box>
          <FieldLabel required>Received</FieldLabel>
          <InvTextField
            value={line.received_quantity}
            onChange={e => handleField('received_quantity', e.target.value)}
            type="number"
            inputProps={{
              min: 1,
              step: 1,
              inputMode: 'numeric',
              ...(pendingInt != null ? { max: pendingInt } : {}),
            }}
            error={Boolean(qtyErr)}
            helperText={qtyErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel>Free Qty</FieldLabel>
          <InvTextField
            value={line.free_quantity ?? '0'}
            onChange={e => handleField('free_quantity', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            error={Boolean(freeQtyErr)}
            helperText={freeQtyErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel required>Rate</FieldLabel>
          <InvTextField
            value={line.unit_rate}
            onChange={e => handleField('unit_rate', e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            error={Boolean(rateErr)}
            helperText={rateErr || poRateHint || undefined}
          />
        </Box>
      </Box>

      {showRateOverride ? (
        <Box sx={{ mb: 1.5 }}>
          <FieldLabel required>Rate Override Reason</FieldLabel>
          <InvTextField
            value={line.rate_override_reason || ''}
            onChange={e => handleField('rate_override_reason', e.target.value)}
            error={Boolean(rateOverrideErr)}
            helperText={rateOverrideErr || 'Required when rate differs from PO'}
          />
        </Box>
      ) : null}

      <Divider sx={{ my: 1.5, borderColor: IPD_COLORS.border }} />
      <SectionTitle>Batch &amp; Dates</SectionTitle>
      <Box sx={{ ...twoCol, mb: 1.5 }}>
        <Box>
          <FieldLabel required={line.is_batch_tracked}>Batch </FieldLabel>
          <InvTextField
            value={line.batch_number}
            onChange={e => handleField('batch_number', e.target.value)}
            error={Boolean(batchErr)}
            helperText={batchErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel>Mfg Batch </FieldLabel>
          <InvTextField
            value={line.manufacturer_batch_number || ''}
            onChange={e => handleField('manufacturer_batch_number', e.target.value)}
          />
        </Box>
        <Box>
          <FieldLabel>Mfg Date</FieldLabel>
          <InvTextField
            value={line.manufacturing_date || ''}
            type="date"
            onChange={e => handleField('manufacturing_date', e.target.value)}
            inputProps={{ max: todayDate }}
            error={Boolean(mfgErr)}
            helperText={mfgErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel required={line.is_expiry_tracked}>Expiry Date</FieldLabel>
          <InvTextField
            value={line.expiry_date || ''}
            type="date"
            onChange={e => handleField('expiry_date', e.target.value)}
            inputProps={{ min: line.allow_expired_override ? undefined : minExpiryDate }}
            error={Boolean(expiryErr)}
            helperText={expiryErr || undefined}
          />
          <ExpiredOverrideControl
            line={line}
            todayDate={todayDate}
            onToggle={checked => handleField('allow_expired_override', checked)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 1.5, borderColor: IPD_COLORS.border }} />
      <SectionTitle>Pricing</SectionTitle>
      <Box sx={{ ...twoCol, mb: 1 }}>
        <Box>
          <FieldLabel required>MRP</FieldLabel>
          <InvTextField
            value={line.mrp || ''}
            onChange={e => handleField('mrp', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            error={Boolean(mrpErr)}
            helperText={mrpErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel>Sale Rate</FieldLabel>
          <InvTextField
            value={line.sale_rate || ''}
            onChange={e => handleField('sale_rate', e.target.value)}
            type="number"
            inputProps={{
              min: line.unit_rate || 0,
              step: 0.01,
              ...(line.mrp ? { max: line.mrp } : {}),
            }}
            error={Boolean(saleErr)}
            helperText={saleErr || (!line.sale_rate ? 'Defaults to MRP when blank' : undefined)}
          />
        </Box>
        <Box>
          <FieldLabel>Tax %</FieldLabel>
          <InvTextField
            value={line.tax_percent ?? '0'}
            onChange={e => handleField('tax_percent', e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 28, step: 0.01 }}
            disabled={taxLocked}
            error={Boolean(errors?.tax_percent)}
            helperText={errors?.tax_percent || (taxLocked ? 'From PO' : undefined)}
          />
        </Box>
        <Box>
          <FieldLabel>Disc %</FieldLabel>
          <DiscPercentField
            value={line.discount_percent ?? '0'}
            onChange={e => handleField('discount_percent', e.target.value)}
            onAdd={onAdd}
            index={index}
            disabled={discLocked}
            error={Boolean(errors?.discount_percent)}
            helperText={errors?.discount_percent || (discLocked ? 'From PO' : undefined)}
          />
        </Box>
      </Box>

      <TotalsBar discAmt={discAmt} rowTotal={rowTotal} />
    </Box>
  );
}

function GrnLineCard(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return <MobileLineCard {...props} />;
  }
  return <DesktopLineCard {...props} />;
}

export default function GrnLineItemsEditor({
  lines,
  isDirect,
  uomMap = {},
  getPoItemOptions,
  getLineErrors,
  showValidationErrors = false,
  canRemoveLine,
  onAddLine,
  onRemoveLine,
  onUpdateLine,
  onPoItemSelect,
  todayDate,
  canOverridePricing = false,
  receivingStoreId,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!lines.length) {
    return (
      <Typography sx={{ color: IPD_COLORS.textSecondary, textAlign: 'center', py: 4 }}>
        No line items — click &quot;Add Row&quot; to begin
      </Typography>
    );
  }

  return (
    <Box sx={{
      ...(!isMobile ? { overflowX: 'auto', pb: 0.5 } : null),
    }}>
      <Box sx={!isMobile ? { minWidth: 960 } : undefined}>
        {lines.map((line, i) => {
          const uomId = line.uom_id;
          const uomLabel = line.uom_name || line.uom_code || (uomId ? uomMap[uomId] : '') || '';
          const receivedInt = parseInt(line.received_quantity, 10) || 0;
          const pendingInt = line.pending_quantity !== '' && line.pending_quantity != null
            ? Math.floor(Number(line.pending_quantity) || 0)
            : null;
          const qtyOverPending = !isDirect && pendingInt != null && receivedInt > pendingInt
            && line.received_quantity !== '';
          const errors = getLineErrors ? getLineErrors(line, i) : {};
          const qtyErr = line.received_quantity && errors?.received_quantity;
          const rateErr = line.unit_rate !== '' && errors?.unit_rate;
          const itemErr = !isDirect && errors?.item_id
            && (line.po_item_id || line.purchase_order_item_id || line.received_quantity || line.unit_rate);
          const batchErr = (line.is_batch_tracked || errors?.batch_number) && errors?.batch_number;
          const expiryErr = (line.is_expiry_tracked || errors?.expiry_date) && errors?.expiry_date;
          const mfgErr = line.manufacturing_date && errors?.manufacturing_date;
          const freeQtyErr = line.free_quantity !== '' && errors?.free_quantity;
          const lineStarted = isDirect
            ? Boolean(line.item_id)
            : Boolean(line.po_item_id || line.purchase_order_item_id);
          const mrpErr = showValidationErrors && lineStarted && errors?.mrp;
          const saleErr = showValidationErrors && lineStarted && errors?.sale_rate;
          const rateOverrideErr = errors?.rate_override_reason;
          const minExpiryDate = minGrnExpiryDate(line.manufacturing_date, todayDate);
          const rowTotal = lineTotal(
            receivedInt,
            line.unit_rate,
            line.tax_percent,
            line.discount_percent,
          );
          const discAmt = computeLineDiscountAmount(
            receivedInt,
            line.unit_rate,
            line.discount_percent,
          );

          return (
            <GrnLineCard
              key={i}
              line={line}
              index={i}
              isDirect={isDirect}
              uomLabel={uomLabel}
              poItemOptions={getPoItemOptions ? getPoItemOptions(i) : []}
              errors={errors}
              canRemove={canRemoveLine}
              onRemove={onRemoveLine}
              onAdd={onAddLine}
              onUpdate={onUpdateLine}
              onPoItemSelect={onPoItemSelect}
              receivedInt={receivedInt}
              pendingInt={pendingInt}
              qtyOverPending={qtyOverPending}
              rowTotal={rowTotal}
              discAmt={discAmt}
              qtyErr={qtyErr}
              rateErr={rateErr}
              itemErr={itemErr}
              batchErr={batchErr}
              expiryErr={expiryErr}
              mfgErr={mfgErr}
              freeQtyErr={freeQtyErr}
              mrpErr={mrpErr}
              saleErr={saleErr}
              rateOverrideErr={rateOverrideErr}
              todayDate={todayDate}
              minExpiryDate={minExpiryDate}
              canOverridePricing={canOverridePricing}
              receivingStoreId={receivingStoreId}
              handleField={(key, val) => {
                onUpdateLine(i, key, val);
                if (key === 'expiry_date' && val && val > todayDate && line.allow_expired_override) {
                  onUpdateLine(i, 'allow_expired_override', false);
                }
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
